// User Group Service
// CRUD operations for user groups

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

export class UserGroupService {
  constructor() {
    this.groupsCollection = collection(db, 'user_groups');
  }
  
  // ==================== CRUD OPERATIONS ====================
  
  /**
   * Create new user group
   */
  async createGroup(groupData, userId) {
    try {
      const now = Timestamp.now();
      const members = groupData.members || [];
      const memberIds = members.map(m => m.userId || m.id).filter(Boolean);

      const group = {
        ...groupData,
        members,
        memberIds,
        isActive: groupData.isActive !== undefined ? groupData.isActive : true,
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now
      };

      const docRef = await addDoc(this.groupsCollection, group);
      console.log(`✅ Created user group: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating user group:', error);
      throw error;
    }
  }
  
  /**
   * Get group by ID
   */
  async getGroup(groupId) {
    try {
      const docRef = doc(this.groupsCollection, groupId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { ...docSnap.data(), id: docSnap.id };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user group:', error);
      throw error;
    }
  }
  
  /**
   * Get all groups with optional filters
   */
  async getAllGroups(filters = {}) {
    try {
      let q = query(this.groupsCollection);
      
      // Apply filters
      if (filters.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }
      
      if (filters.groupType) {
        q = query(q, where('groupType', '==', filters.groupType));
      }
      
      if (filters.departmentId) {
        q = query(q, where('departmentId', '==', filters.departmentId));
      }
      
      if (filters.shiftType) {
        q = query(q, where('shiftType', '==', filters.shiftType));
      }
      
      // Order by name
      q = query(q, orderBy('name', 'asc'));
      
      const snapshot = await getDocs(q);
      let groups = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }));
      
      // Apply search term filter (client-side)
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        groups = groups.filter(g => 
          g.name.toLowerCase().includes(searchLower) ||
          g.description.toLowerCase().includes(searchLower)
        );
      }
      
      return groups;
    } catch (error) {
      console.error('Error fetching user groups:', error);
      throw error;
    }
  }
  
  /**
   * Get groups by department
   */
  async getGroupsByDepartment(departmentId) {
    try {
      const q = query(
        this.groupsCollection,
        where('isActive', '==', true),
        where('departmentId', '==', departmentId),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }));
    } catch (error) {
      console.error('Error fetching groups by department:', error);
      throw error;
    }
  }
  
  /**
   * Get groups by shift type
   */
  async getGroupsByShift(shiftType) {
    try {
      const q = query(
        this.groupsCollection,
        where('isActive', '==', true),
        where('shiftType', '==', shiftType),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }));
    } catch (error) {
      console.error('Error fetching groups by shift:', error);
      throw error;
    }
  }
  
  /**
   * Update user group
   */
  async updateGroup(groupId, updates, userId) {
    try {
      const docRef = doc(this.groupsCollection, groupId);
      const payload = { ...updates, updatedBy: userId, updatedAt: Timestamp.now() };
      if (Array.isArray(updates.members)) {
        payload.memberIds = updates.members.map(m => m.userId || m.id).filter(Boolean);
      }
      await updateDoc(docRef, payload);
      
      console.log(`✅ Updated user group: ${groupId}`);
    } catch (error) {
      console.error('Error updating user group:', error);
      throw error;
    }
  }
  
  /**
   * Delete user group (soft delete)
   */
  async deleteGroup(groupId, userId) {
    try {
      await this.updateGroup(groupId, { isActive: false }, userId);
      console.log(`✅ Deleted user group: ${groupId}`);
    } catch (error) {
      console.error('Error deleting user group:', error);
      throw error;
    }
  }
  
  /**
   * Hard delete user group
   */
  async hardDeleteGroup(groupId) {
    try {
      const docRef = doc(this.groupsCollection, groupId);
      await deleteDoc(docRef);
      console.log(`✅ Hard deleted user group: ${groupId}`);
    } catch (error) {
      console.error('Error hard deleting user group:', error);
      throw error;
    }
  }
  
  // ==================== MEMBER MANAGEMENT ====================
  
  /**
   * Add member to group
   */
  async addMember(groupId, member, userId) {
    try {
      const group = await this.getGroup(groupId);
      if (!group) throw new Error('Group not found');
      
      // Check if member already exists
      const existingMember = group.members.find(m => m.userId === member.userId);
      if (existingMember) {
        throw new Error('Member already exists in group');
      }
      
      const updatedMembers = [...group.members, {
        ...member,
        addedAt: Timestamp.now(),
        addedBy: userId
      }];
      
      await this.updateGroup(groupId, { members: updatedMembers }, userId);
      console.log(`✅ Added member to group: ${groupId}`);
    } catch (error) {
      console.error('Error adding member to group:', error);
      throw error;
    }
  }
  
  /**
   * Remove member from group
   */
  async removeMember(groupId, memberId, userId) {
    try {
      const group = await this.getGroup(groupId);
      if (!group) throw new Error('Group not found');
      
      const updatedMembers = group.members.filter(m => m.userId !== memberId);
      
      await this.updateGroup(groupId, { members: updatedMembers }, userId);
      console.log(`✅ Removed member from group: ${groupId}`);
    } catch (error) {
      console.error('Error removing member from group:', error);
      throw error;
    }
  }
  
  /**
   * Update member in group
   */
  async updateMember(groupId, memberId, memberUpdates, userId) {
    try {
      const group = await this.getGroup(groupId);
      if (!group) throw new Error('Group not found');
      
      const updatedMembers = group.members.map(m => 
        m.userId === memberId ? { ...m, ...memberUpdates } : m
      );
      
      await this.updateGroup(groupId, { members: updatedMembers }, userId);
      console.log(`✅ Updated member in group: ${groupId}`);
    } catch (error) {
      console.error('Error updating member in group:', error);
      throw error;
    }
  }
  
  // ==================== ASSIGNMENT LOGIC ====================
  
  /**
   * Get available members in group (not at capacity)
   */
  async getAvailableMembers(groupId, currentTaskCounts = {}) {
    try {
      const group = await this.getGroup(groupId);
      if (!group) throw new Error('Group not found');
      
      return group.members.filter(member => {
        const count = currentTaskCounts[member.userId] || 0;
        return count < group.assignmentRules.maxConcurrentTasks;
      });
    } catch (error) {
      console.error('Error getting available members:', error);
      throw error;
    }
  }
  
  /**
   * Get next member for assignment based on workload balancing method
   */
  async getNextMember(groupId, currentTaskCounts = {}, lastAssignedIndex = 0) {
    try {
      const group = await this.getGroup(groupId);
      if (!group) throw new Error('Group not found');
      
      const availableMembers = await this.getAvailableMembers(groupId, currentTaskCounts);
      
      if (availableMembers.length === 0) {
        return null;
      }
      
      const method = group.assignmentRules.workloadBalancing || 'round_robin';
      
      switch (method) {
        case 'round_robin':
          return availableMembers[lastAssignedIndex % availableMembers.length];
        
        case 'least_busy':
          return availableMembers.reduce((least, member) => {
            const memberCount = currentTaskCounts[member.userId] || 0;
            const leastCount = currentTaskCounts[least.userId] || 0;
            return memberCount < leastCount ? member : least;
          });
        
        case 'skill_based':
          // TODO: Implement skill matching in Phase 4
          return availableMembers[0];
        
        case 'priority_based':
          // Return primary members first
          const primaryMembers = availableMembers.filter(m => m.isPrimary);
          return primaryMembers.length > 0 ? primaryMembers[0] : availableMembers[0];
        
        case 'manual':
          return null; // Manual assignment requires supervisor selection
        
        default:
          return availableMembers[0];
      }
    } catch (error) {
      console.error('Error getting next member:', error);
      throw error;
    }
  }
  
  /**
   * Check if group is available at given time
   */
  isGroupAvailable(group, dateTime) {
    const date = dateTime.toDate ? dateTime.toDate() : new Date(dateTime);
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    
    // Check if day is in schedule
    if (!group.schedule.daysOfWeek.includes(dayOfWeek)) {
      return false;
    }
    
    // Check if time is within schedule
    const time = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    return time >= group.schedule.startTime && time <= group.schedule.endTime;
  }
  
  // ==================== STATISTICS ====================
  
  /**
   * Get IDs of groups that contain the given user as a member.
   * Uses indexed memberIds when present (fast); falls back to getAllGroups for groups without memberIds.
   * Returns [] on error so callers (e.g. My Tasks) still get direct-assigned tasks and don't hang.
   */
  async getGroupIdsForUser(userId) {
    try {
      const q = query(
        this.groupsCollection,
        where('isActive', '==', true),
        where('memberIds', 'array-contains', userId)
      );
      const snapshot = await getDocs(q);
      const groupIds = snapshot.docs.map(d => d.id);
      if (groupIds.length > 0) return groupIds;
      // Fallback for groups that don't have memberIds backfilled yet
      const groups = await this.getAllGroups({ isActive: true });
      return (groups || [])
        .filter(g => (g.members || []).some(m => (m.userId || m.id) === userId))
        .map(g => g.id);
    } catch (error) {
      console.error('Error fetching group IDs for user:', error);
      return [];
    }
  }

  /**
   * Get group statistics
   */
  async getGroupStats(groupId) {
    try {
      const group = await this.getGroup(groupId);
      if (!group) throw new Error('Group not found');
      
      return {
        memberCount: group.members.length,
        primaryMembers: group.members.filter(m => m.isPrimary).length,
        backupMembers: group.members.filter(m => m.isBackup).length,
        supervisorCount: group.supervisors.length,
        isActive: group.isActive,
        maxConcurrentTasks: group.assignmentRules.maxConcurrentTasks,
        maxDailyTasks: group.assignmentRules.maxDailyTasks
      };
    } catch (error) {
      console.error('Error fetching group stats:', error);
      throw error;
    }
  }
}

export const userGroupService = new UserGroupService();


